import { useDispatch, useSelector } from 'react-redux'
import { selectPrimarySecurity } from '../../../../selectors/home'
import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
    alpha,
    Box,
    Checkbox,
    Chip,
    Divider,
    FormControl,
    FormControlLabel,
    InputAdornment,
    List,
    Menu,
    MenuItem,
    Tab,
    Tabs,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material'
import { SearchParamsContext } from '../../../../components/providers/searchParamsProvider'
import { selectUnderlyingOptionsExpiriesV2 } from '../../../../selectors/options'
import { getSecurityKey, isFnOSecurity } from '../../../../util/security'
import {
    CallPutType,
    MultiStrikeSecuritiesType,
    setMultiStrikeChartStyles,
    setMultiStrikeNearAtm,
    setMultiStrikeSecurities
} from '../../../../reducer/multiStrikeChart.reducer'
import {
    selectMultiStrikeAtm,
    selectMultiStrikeChartSecurities,
    selectMultiStrikeChartStyles,
    selectMultiStrikeLoading,
    selectMultiStrikeNearAtm
} from '../../../../selectors/multiStrikeChart'
import { AppContext } from '../../../../App'
import { ExtraSmallButton } from '../../../../components/custom'
import AnalyticsManager from '../../../../analytics/AnalyticsManager'
import { Check, FormatListBulleted, GpsFixed, SearchOutlined } from '@mui/icons-material'
import { updateMultiStrikeChartConfig } from '../../../../reducer/chart.reducer'
import { ChartViewKey } from '../../../../assets/constants'
import {
    selectMultiStrikeChartConfigAtm,
    selectMultiStrikeChartConfigExpiry,
    selectMultiStrikeChartConfigNearAtm,
    selectMultiStrikeChartConfigSecurities,
    selectMultiStrikeChartConfigStyles
} from '../../../../selectors/chart'
import { DropDownStrike } from '../../../../reducer/options.reducer'
import { findNearestNumber } from '../../../../util/tradepanel'
import { getSecuritySnapshot } from '../../../../selectors/security'
import MultiStrikesAtmSideEffects from './MultiStrikesAtmSideEffects'
import ToggleSwitch from '../../../../components/common/toggleSwitch/toggleSwitch'
import { useWidgetSettingsMenuProps } from '../../../../components/widgetDashboard/widgetSettingsMenuContext'

export const getStrikeAndStrikeData = (atm: DropDownStrike, priceMultiplier: number, isDecimal: boolean) => {
    const atmStrike = (atm.strike / priceMultiplier)
    const strike = isDecimal ? atmStrike.toFixed(2) : atmStrike.toString()
    const strikeData = {
        callSecKey: getSecurityKey(atm.callSecurity.exchange, atm.callSecurity.code),
        putSecKey: getSecurityKey(atm.putSecurity.exchange, atm.putSecurity.code),
        callPut: ['CE', 'PE'] as CallPutType[]
    }
    return { strike, strikeData }
}

const MultiStrikesMenu = ({
    viewKey,
    notificationContainer,
    fullWidth = false,
}: {
    viewKey: string
    notificationContainer?: HTMLElement | null
    fullWidth?: boolean
}) => {
    const dispatch = useDispatch()
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const menuProps = useWidgetSettingsMenuProps('left')
    const open = Boolean(anchorEl)
    const { searchParams } = useContext(SearchParamsContext)!
    const appContext = useContext(AppContext)
    const getChart = useCallback(() => appContext.getMultiStrikeChart(viewKey), [appContext, viewKey])
    const isWidget = useMemo(() => viewKey !== ChartViewKey.MULTI_STRIKES_CHART, [viewKey])
    const urlexpiry = decodeURIComponent(searchParams.get('expiry') || '')
    const selectConfigExpiry = useMemo(() => selectMultiStrikeChartConfigExpiry(viewKey), [viewKey])
    const stateExpiry = useSelector(selectConfigExpiry)
    const expiry = useMemo(() => (isWidget ? stateExpiry : urlexpiry), [isWidget, stateExpiry, urlexpiry])
    const { primary, underlying } = useSelector(selectPrimarySecurity)
    const mainSecurity = primary && isFnOSecurity(primary) ? underlying : primary
    const underlyingToken = getSecurityKey(mainSecurity?.exchange ?? 0, mainSecurity?.code ?? '')
    const selectExpiries = useMemo(() => selectUnderlyingOptionsExpiriesV2(underlyingToken), [underlyingToken])
    const optionsExpiries = useSelector(selectExpiries)
    const selectNearAtm = useMemo(
        () => (isWidget ? selectMultiStrikeChartConfigNearAtm(viewKey) : selectMultiStrikeNearAtm),
        [isWidget, viewKey]
    )
    const selectAtm = useMemo(
        () => (isWidget ? selectMultiStrikeChartConfigAtm(viewKey) : selectMultiStrikeAtm),
        [isWidget, viewKey]
    )
    const nearAtm = useSelector(selectNearAtm)
    const atmSelected = useSelector(selectAtm)
    const selectStyles = useMemo(
        () => (isWidget ? selectMultiStrikeChartConfigStyles(viewKey) : selectMultiStrikeChartStyles),
        [isWidget, viewKey]
    )
    const styles = useSelector(selectStyles)
    const { autoRefresh, notifyInterval } = styles
    const nearAtmCount = styles.nearAtmCount ?? 3
    const [activeTab, setActiveTab] = useState<'nearAtm' | 'manual'>('nearAtm')
    const [searchText, setSearchText] = useState('')
    const listRefs = useRef<(HTMLLIElement | null)[]>([])
    const scrolledToSelected = useRef(false)
    const loading = useSelector(selectMultiStrikeLoading)
    const [roundingStrikesRequired, setRoundingStrikesRequired] = useState(false)
    const selectSecurities = useMemo(
        () => (isWidget ? selectMultiStrikeChartConfigSecurities(viewKey) : selectMultiStrikeChartSecurities),
        [isWidget, viewKey]
    )
    const multiStrikeChartSecurities = useSelector(selectSecurities)

    useEffect(() => {
        setRoundingStrikesRequired(false)
    }, [underlyingToken])

    const multiStrikeChartSecuritiesKeys = useMemo(
        () => Object.keys(multiStrikeChartSecurities ?? {}),
        [multiStrikeChartSecurities]
    )

    const ivStrikesMenu = useMemo(() => {
        const expiryIndex = optionsExpiries?.findIndex(o => o.expiry.name === expiry)
        if (expiryIndex !== undefined && expiryIndex >= 0) {
            const expiryData = optionsExpiries?.[expiryIndex]
            if (expiryData) {
                const data = expiryData.strikes.map((s) => {
                    const price = (s.strike) / (mainSecurity?.priceMultiplier ?? 100)
                    if (!Number.isInteger(price) && !roundingStrikesRequired) setRoundingStrikesRequired(true)
                    return {
                        label: !roundingStrikesRequired ? price.toString() : price.toFixed(2),
                        callValue: getSecurityKey(s.callSecurity.exchange ?? 0, s.callSecurity?.code ?? ''),
                        putValue: getSecurityKey(s.putSecurity?.exchange ?? 0, s.putSecurity?.code ?? '')
                    }
                })
                return !searchText ? data : data.filter(d => d.label.includes(searchText))
            }
        }
        return []
    }, [expiry, mainSecurity, optionsExpiries, searchText, roundingStrikesRequired])

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        AnalyticsManager.logEvent('Strike Button Clicked', { source: 'multistrike' })
        setActiveTab(nearAtm ? 'nearAtm' : 'manual')
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const clearNearAtm = useCallback(() => {
        if (isWidget) {
            dispatch(updateMultiStrikeChartConfig({ viewKey, config: { nearAtm: undefined, atm: undefined } }))
        } else {
            dispatch(setMultiStrikeNearAtm({ nearAtm: undefined, atm: undefined }))
        }
    }, [dispatch, isWidget, viewKey])

    const handleAutoRefresh = useCallback((checked: boolean) => {
        AnalyticsManager.logEvent('Setting Auto Refresh Toggled', {
            source: 'multistrike',
            type: 'autoRefresh',
            value: checked
        })
        const nextStyles = { ...styles, autoRefresh: checked }
        if (isWidget) {
            dispatch(updateMultiStrikeChartConfig({ viewKey, config: { styles: nextStyles } }))
        } else {
            dispatch(setMultiStrikeChartStyles(nextStyles))
        }
    }, [dispatch, isWidget, styles, viewKey])

    const persistNearAtmCount = useCallback((count: number) => {
        if (styles.nearAtmCount === count) return
        const nextStyles = { ...styles, nearAtmCount: count }
        if (isWidget) {
            dispatch(updateMultiStrikeChartConfig({ viewKey, config: { styles: nextStyles } }))
        } else {
            dispatch(setMultiStrikeChartStyles(nextStyles))
        }
    }, [dispatch, isWidget, styles, viewKey])

    const scrollSelectedInView = useCallback((multiStrikeChartSecuritiesPassed = multiStrikeChartSecurities) => {
        if (anchorEl) {
            const minselectedValue = Math.min(...Object.keys(multiStrikeChartSecuritiesPassed ?? {}).map(v => Number(v)))
            const index = ivStrikesMenu.findIndex(i => i.label === (roundingStrikesRequired ? minselectedValue.toFixed(2) : minselectedValue.toString()))
            setTimeout(() => {
                if (index >= 0 && listRefs.current[index] && !scrolledToSelected.current) {
                    listRefs.current[index]?.scrollIntoView({
                        block: 'start'
                    })
                    scrolledToSelected.current = true
                }
            }, 100)
        } else {
            scrolledToSelected.current = false
        }
    }, [anchorEl, ivStrikesMenu, multiStrikeChartSecurities, roundingStrikesRequired])

    useEffect(() => {
        if (activeTab === 'manual') {
            scrollSelectedInView()
        }
    }, [scrollSelectedInView, activeTab])

    const handleSearchTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { value } = e.target
        if (/^\d+$/.test(value)) {
            setSearchText(value)
        } else if (!value) {
            setSearchText('')
        }
    }

    const addOrRemoveSecurity = useCallback((strike: string, strikeData: {
        callSecKey: string,
        putSecKey: string,
        callPut: CallPutType[]
    }) => {
        const chart = getChart()
        const data = { ...multiStrikeChartSecurities }

        if (data[strike]) {
            delete data[strike]
            chart?.dataManager.removeStrike(strike)
            AnalyticsManager.logEvent('Strike Removed', { strike: strike, source: 'multistrike' })
        } else {
            if (multiStrikeChartSecuritiesKeys.length < 10) {
                data[strike] = strikeData.callPut
                chart?.dataManager.addNewSecurities(strike, strikeData)
                AnalyticsManager.logEvent('Strike Added', { strike: strike, source: 'multistrike' })
            }
        }
        if (isWidget) {
            dispatch(updateMultiStrikeChartConfig({ viewKey, config: { securities: data } }))
        } else {
            dispatch(setMultiStrikeSecurities(data))
        }
        setTimeout(() => {
            chart?.onCrosshairMove({} as any)
        }, 500)
    }, [multiStrikeChartSecurities, isWidget, getChart, multiStrikeChartSecuritiesKeys.length, dispatch, viewKey])

    const handleAtmStrikeClick = useCallback(async (i: number, driftedAtmStrike?: number) => {
        AnalyticsManager.logEvent('Near ATM Strike Selected', { numberOfStrike: i, source: 'stockbar' })
        const chart = getChart()
        if (!chart) return
            const expiryIndex = optionsExpiries?.findIndex(o => o.expiry.name === expiry)
            if (expiryIndex !== undefined && expiryIndex >= 0) {
                const expiryData = optionsExpiries?.[expiryIndex]
                if (expiryData) {
                    const underlyingLtp = getSecuritySnapshot(underlyingToken)?.lastTradedPrice
                    const atmStrike =
                        driftedAtmStrike ??
                        findNearestNumber(expiryData.strikesArray, underlyingLtp ?? 0)
                    const atm = expiryData.strikes.find(s => s.strike === atmStrike)
                    const atmIndex = expiryData.strikes.findIndex(s => s.strike === atmStrike)
                    if (atm) {
                        if (!driftedAtmStrike && atmSelected === atm.strike && i === nearAtm) return
                        if (isWidget) {
                            dispatch(updateMultiStrikeChartConfig({
                                viewKey, config: {
                                    nearAtm: i,
                                    atm: atm.strike
                                }
                            }))
                        } else {
                            dispatch(setMultiStrikeNearAtm({
                                nearAtm: i,
                                atm: atm.strike
                            }))
                        }
                        const {
                            strike,
                            strikeData
                        } = getStrikeAndStrikeData(atm, mainSecurity?.priceMultiplier ?? 100, roundingStrikesRequired)
                        let data = {
                            [strike]: strikeData
                        }
                        const a = [3, 5, 7, 9]
                        a.forEach((x) => {
                            if (i >= x) {
                                const index = x === 3 ? 1 : x === 5 ? 2 : x === 7 ? 3 : 4
                                const atm1 = expiryData.strikes[atmIndex - index]
                                const atm2 = expiryData.strikes[atmIndex + index]
                                const {
                                    strike: strike1,
                                    strikeData: strikeData1
                                } = getStrikeAndStrikeData(atm1, mainSecurity?.priceMultiplier ?? 100, roundingStrikesRequired)
                                const {
                                    strike: strike2,
                                    strikeData: strikeData2
                                } = getStrikeAndStrikeData(atm2, mainSecurity?.priceMultiplier ?? 100, roundingStrikesRequired)
                                data = {
                                    [strike1]: strikeData1,
                                    ...data,
                                    [strike2]: strikeData2
                                }
                            }
                        })
                        chart.dataManager.removeAllSecondarySecurities()
                        const formattedData: MultiStrikeSecuritiesType = Object.fromEntries(
                            Object.entries(data).map(([key, value]) => [key, value.callPut])
                        );
                        if (isWidget) {
                            dispatch(updateMultiStrikeChartConfig({ viewKey, config: {
                                nearAtm: i,
                                securities: formattedData 
                            }}))
                        } else {
                            dispatch(setMultiStrikeSecurities(formattedData))
                        }
                        await chart.dataManager.addNewStrikes(data)
                        chart.onCrosshairMove({} as any)
                        chart.resetTimeScale()
                        requestAnimationFrame(() => {
                            requestAnimationFrame(() => chart.restoreAutoscroll())
                        })
                        scrolledToSelected.current = false
                        scrollSelectedInView(formattedData)
                    }
                }
            }
    }, [getChart, optionsExpiries, expiry, underlyingToken, atmSelected, nearAtm, isWidget, mainSecurity?.priceMultiplier, roundingStrikesRequired, scrollSelectedInView, dispatch, viewKey])

    const handleNearAtmCountChange = useCallback((count: number) => {
        persistNearAtmCount(count)
        handleAtmStrikeClick(count)
    }, [persistNearAtmCount, handleAtmStrikeClick])

    const handleTabChange = useCallback((tab: 'nearAtm' | 'manual') => {
        setActiveTab(tab)
        if (tab === 'nearAtm') {
            if (!nearAtm) {
                handleAtmStrikeClick(nearAtmCount)
            }
        } else if (nearAtm) {
            clearNearAtm()
        }
    }, [nearAtm, nearAtmCount, handleAtmStrikeClick, clearNearAtm])

    return (
        <>
            <MultiStrikesAtmSideEffects
                underlyingToken={underlyingToken}
                expiry={expiry}
                nearAtm={nearAtm}
                atmSelected={atmSelected}
                autoRefresh={autoRefresh}
                notifyInterval={notifyInterval}
                priceMultiplier={mainSecurity?.priceMultiplier ?? 100}
                onRefreshNearAtm={handleAtmStrikeClick}
                notificationContainer={notificationContainer}
            />
            <FormControl sx={{ width: fullWidth ? '100%' : undefined, minWidth: fullWidth ? undefined : '70px', height: '32px', flexShrink: fullWidth ? undefined : 0 }}>
                <ExtraSmallButton
                    color="inherit"
                    id="multistrike-strike-menu-button"
                    variant="outlined"
                    aria-controls={open ? 'basic-menu' : undefined}
                    aria-haspopup="true"
                    sx={{
                        height: '100%',
                        width: fullWidth ? '100%' : undefined,
                        textWrap: 'nowrap',
                        flexShrink: fullWidth ? undefined : 0,
                        borderColor: theme => theme.palette.background.default
                    }}
                    aria-expanded={open ? 'true' : undefined}
                    onClick={handleClick}
                >
                    {nearAtm ? `Near ATM ${nearAtm}` : `${multiStrikeChartSecuritiesKeys.length || ''} Strike${multiStrikeChartSecuritiesKeys.length > 1 ? 's' : ''}`}
                </ExtraSmallButton>
                <Menu
                    key={'multistrike-menu'}
                    id="demo-multiple-checkbox"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    {...menuProps}
                    MenuListProps={{ sx: { p: 0 } }}
                >
                    <Box sx={{ width: 300 }}>
                        <Tabs
                            value={activeTab}
                            onChange={(_, value) => {
                                if (value === 'manual') {
                                    scrolledToSelected.current = false
                                }
                                handleTabChange(value)
                            }}
                            variant="fullWidth"
                            sx={{ borderBottom: theme => `1px solid ${theme.palette.divider}`, px: 1 }}
                        >
                            <Tab
                                value="nearAtm"
                                icon={<GpsFixed sx={{ fontSize: 16 }} />}
                                iconPosition="start"
                                label="Near ATM"
                            />
                            <Tab
                                value="manual"
                                icon={<FormatListBulleted sx={{ fontSize: 16 }} />}
                                iconPosition="start"
                                label="Manual"
                            />
                        </Tabs>

                        {activeTab === 'nearAtm' && (
                            <Box sx={{ p: 2 }}>
                                <Typography
                                    variant="overline"
                                    color="text.secondary"
                                    sx={{ display: 'block', mb: 1, lineHeight: 1.5 }}
                                >
                                    Near ATM
                                </Typography>
                                <ToggleButtonGroup
                                    size="small"
                                    exclusive
                                    fullWidth
                                    value={nearAtm ?? nearAtmCount}
                                    onChange={(_, value) => value && handleNearAtmCountChange(value)}
                                    sx={{
                                        height: 36,
                                        '& .MuiToggleButton-root': {
                                            '&:hover': {
                                                backgroundColor: theme => alpha(theme.palette.text.primary, 0.04)
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: theme => alpha(theme.palette.text.primary, 0.08),
                                                '&:hover': {
                                                    backgroundColor: theme => alpha(theme.palette.text.primary, 0.12)
                                                }
                                            }
                                        }
                                    }}
                                >
                                    {[1, 3, 5, 7, 9].map((i) => (
                                        <ToggleButton key={i} value={i}>
                                            {i}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                                <Divider sx={{ my: 1 }} />
                                <FormControlLabel
                                    labelPlacement="start"
                                    disableTypography
                                    onChange={() => handleAutoRefresh(!autoRefresh)}
                                    sx={{
                                        m: 0,
                                        width: '100%',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}
                                    control={
                                        <ToggleSwitch
                                            size="small"
                                            checked={autoRefresh}
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="body2" color="text.primary">
                                                Auto-refresh strikes
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Every 30 seconds
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Box>
                        )}

                        {activeTab === 'manual' && (
                            <Box sx={{ p: 2, pt: 1.5 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        mb: 1
                                    }}
                                >
                                    <Typography
                                        variant="overline"
                                        color="text.secondary"
                                        sx={{ lineHeight: 1.5 }}
                                    >
                                        Fixed Strikes
                                    </Typography>
                                    {multiStrikeChartSecuritiesKeys.length > 0 && (
                                        <Chip
                                            size="small"
                                            icon={<Check sx={{ fontSize: 14 }} />}
                                            label={`${multiStrikeChartSecuritiesKeys.length} selected`}
                                        />
                                    )}
                                </Box>
                                <TextField
                                    fullWidth
                                    value={searchText}
                                    onChange={handleSearchTextChange}
                                    placeholder="Search strikes..."
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchOutlined fontSize="small" />
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={{ mb: 1 }}
                                />
                                <List sx={{ maxHeight: '40vh', overflowY: 'auto', p: 0 }}>
                                    {
                                        ivStrikesMenu.length > 0 &&
                                        ivStrikesMenu?.map((o: any, i: number) => {
                                            const selected = !!multiStrikeChartSecurities?.[o.label]
                                            return (
                                                <MenuItem
                                                    key={o.label}
                                                    ref={(el) => {
                                                        listRefs.current[i] = el
                                                    }}
                                                    disabled={loading}
                                                    onClick={() => {
                                                        if (loading) return
                                                        addOrRemoveSecurity(o.label, {
                                                            callSecKey: o.callValue,
                                                            putSecKey: o.putValue,
                                                            callPut: ['CE', 'PE'] as CallPutType[]
                                                        })
                                                    }}
                                                    selected={selected}
                                                    sx={{
                                                        height: 40,
                                                        px: 0,
                                                        borderBottom: theme => `1px solid ${theme.palette.divider}`,
                                                        '&:hover': {
                                                            backgroundColor: theme => alpha(theme.palette.text.primary, 0.04)
                                                        },
                                                        '&.Mui-selected': {
                                                            backgroundColor: theme => alpha(theme.palette.text.primary, 0.06)
                                                        },
                                                        '&.Mui-selected:hover': {
                                                            backgroundColor: theme => alpha(theme.palette.text.primary, 0.1)
                                                        }
                                                    }}
                                                >
                                                    <Checkbox
                                                        sx={{ '&:hover': { bgcolor: 'transparent' } }}
                                                        checked={selected}
                                                    />
                                                    <Typography>
                                                        {o.label}
                                                    </Typography>
                                                </MenuItem>
                                            )
                                        })}
                                </List>
                            </Box>
                        )}
                    </Box>
                </Menu>
            </FormControl>
        </>
    )
}


export default memo(MultiStrikesMenu)